import React from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Option {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface AppSelectProps {
  value?: string | number;
  onChange: (value: string | number) => void;
  options: Option[];
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  colorScheme?: "blue" | "purple" | "green" | "amber" | "default";
  clearable?: boolean;
}

export const AppSelect: React.FC<AppSelectProps> = ({
  value,
  onChange,
  options,
  isLoading,
  placeholder,
  label,
  colorScheme = "default",
  clearable = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (value && typeof value === "number") {
        onChange(0);
      } else {
        onChange("");
      }
      setOpen(false);
    },
    [onChange]
  );

  // Color scheme styles
  const getColorStyles = () => {
    switch (colorScheme) {
      case "blue":
        return {
          button: "border-blue-200 hover:border-blue-300 focus:ring-blue-500",
          selected: "bg-blue-50 text-blue-700",
          icon: "text-blue-500",
        };
      case "purple":
        return {
          button: "border-purple-200 hover:border-purple-300 focus:ring-purple-500",
          selected: "bg-purple-50 text-purple-700",
          icon: "text-purple-500",
        };
      case "green":
        return {
          button: "border-green-200 hover:border-green-300 focus:ring-green-500",
          selected: "bg-green-50 text-green-700",
          icon: "text-green-500",
        };
      case "amber":
        return {
          button: "border-amber-200 hover:border-amber-300 focus:ring-amber-500",
          selected: "bg-amber-50 text-amber-700",
          icon: "text-amber-500",
        };
      default:
        return {
          button: "",
          selected: "bg-accent",
          icon: "",
        };
    }
  };

  const colorStyles = getColorStyles();

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between relative min-w-[200px]",
            value && "text-foreground",
            colorStyles.button
          )}
          disabled={isLoading}
        >
          {value ? (
            <div className="flex items-center">
              {clearable && (
                <span
                  className="cursor-pointer hover:text-primary text-muted-foreground mr-2"
                  onClick={handleClear}
                  aria-label="Clear selection"
                >
                  <X className="h-4 w-4" />
                </span>
              )}
              {options.find((option) => option.value === value)?.icon && (
                <span className="mr-2">{options.find((option) => option.value === value)?.icon}</span>
              )}
              {options.find((option) => option.value === value)?.label || placeholder}
            </div>
          ) : (
            placeholder
          )}
          <ChevronsUpDown
            className={cn(
              "h-4 w-4 shrink-0 opacity-50 absolute right-2",
              colorScheme !== "default" && colorStyles.icon
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder={t("common.search")} />
          <CommandList>
            <CommandEmpty>{t("dataTable.noResults")}</CommandEmpty>
            {value ? (
              <CommandGroup heading={t("common.selected")}>
                {options
                  .filter((option) => option.value === value)
                  .map((option) => (
                    <CommandItem
                      key={option.value.toString()}
                      value={option.label}
                      onSelect={() => {
                        onChange(option.value);
                        setOpen(false);
                      }}
                      className={cn("cursor-pointer", colorStyles.selected)}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4 opacity-100", colorScheme !== "default" && colorStyles.icon)}
                      />
                      {option.icon && <span className="mr-2">{option.icon}</span>}
                      {option.label}
                    </CommandItem>
                  ))}
              </CommandGroup>
            ) : null}
            <CommandGroup heading={label}>
              {options
                .filter((option) => option.value !== value)
                .map((option) => (
                  <CommandItem
                    key={option.value.toString()}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    {option.icon && <span className="mr-2">{option.icon}</span>}
                    {option.label}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
